import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { formatMoney } from '@mavatech/shared'
import { trpc } from '@/lib/trpc'

export function OverviewTab({ year }: { year: number }) {
  const q = trpc.report.summary.useQuery({ year })
  if (q.isLoading || !q.data) return <p className="p-6 text-sm text-text-tertiary">Lädt …</p>
  const d = q.data

  const chart = d.months.map((m) => ({
    name: m.label,
    Einnahmen: m.einnahmenNet / 100,
    Ausgaben: m.ausgabenNet / 100,
  }))

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <div className="grid grid-cols-3 gap-4">
        <div className="stat-tile">
          <span className="text-xs text-text-tertiary uppercase tracking-wide">Einnahmen (netto)</span>
          <span className="text-2xl font-semibold text-text-primary text-money">{formatMoney(d.totals.einnahmenNet)}</span>
        </div>
        <div className="stat-tile">
          <span className="text-xs text-text-tertiary uppercase tracking-wide">Ausgaben (netto)</span>
          <span className="text-2xl font-semibold text-text-primary text-money">{formatMoney(d.totals.ausgabenNet)}</span>
        </div>
        <div className="stat-tile">
          <span className="text-xs text-text-tertiary uppercase tracking-wide">Überschuss (netto)</span>
          <span className="text-2xl font-semibold text-money" style={{ color: d.totals.ueberschussNet >= 0 ? 'rgb(var(--color-success))' : 'rgb(var(--color-danger))' }}>
            {formatMoney(d.totals.ueberschussNet)}
          </span>
        </div>
      </div>

      <div className="card p-5">
        <p className="text-sm font-medium text-text-primary mb-3">Einnahmen &amp; Ausgaben je Monat (netto)</p>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
              <CartesianGrid stroke="rgb(var(--color-border-subtle))" vertical={false} />
              <XAxis dataKey="name" tick={{ fill: 'rgb(var(--color-text-tertiary))', fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fill: 'rgb(var(--color-text-tertiary))', fontSize: 11 }} tickLine={false} axisLine={false} width={60} tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                contentStyle={{ background: 'rgb(var(--color-bg-card))', border: '1px solid rgb(var(--color-border-default))', borderRadius: 8, fontSize: 12 }}
                formatter={(v: number) => formatMoney(Math.round(v * 100))}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Einnahmen" fill="rgb(var(--color-accent))" radius={[3, 3, 0, 0]} maxBarSize={22} />
              <Bar dataKey="Ausgaben" fill="rgb(var(--color-warning))" radius={[3, 3, 0, 0]} maxBarSize={22} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card p-5">
        <p className="text-sm font-medium text-text-primary mb-3">Umsatzsteuer je Quartal ({d.besteuerungsart === 'ist' ? 'Ist' : 'Soll'}-Besteuerung)</p>
        <div className="overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th>Quartal</th>
              <th className="text-right">USt vereinnahmt</th>
              <th className="text-right">Vorsteuer</th>
              <th className="text-right">Zahllast</th>
            </tr>
          </thead>
          <tbody>
            {d.quarters.map((qz) => (
              <tr key={qz.quarter}>
                <td className="text-text-secondary">Q{qz.quarter}</td>
                <td className="text-right text-money">{formatMoney(qz.ustCents)}</td>
                <td className="text-right text-money">{formatMoney(qz.vorsteuerCents)}</td>
                <td className="text-right text-money font-medium text-text-primary">{formatMoney(qz.zahllastCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <p className="text-2xs text-text-tertiary mt-2">
          Näherungswert. Maßgeblich ist die UVA je Voranmeldungszeitraum (Tab „UVA"). Kennzahlen mit dem Steuerberater bestätigen.
        </p>
      </div>
    </div>
  )
}
